import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Hidden,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { toggleNavCollapsed } from "../../redux/actions";
import { getRisOrgs } from "../../redux/actions/RisAdmin";
import { fontColor } from "../../constants/ColorSets";
import { isCurrentAccountAdmin } from "../../components/AppBase/AppAuthProvider";
import AppLoader from "../../components/AppBase/AppLoader";
import AppInfoView from "../../components/AppBase/AppInfoView";
import EmptyResult from "../rules/EmptyResult";
import { RisOrg } from "../../types/models/RisOrg";
import OrgDialog from "./OrgDialog";

/**
 * Admin-only list of RIS organisations, with create and edit.
 */
const OrganisationsPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isScreenLessThanSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState<boolean>(false);
  const [orgs, setOrgs] = useState<RisOrg[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [editOrg, setEditOrg] = useState<RisOrg>();
  const [openOrgDialog, setOpenOrgDialog] = useState<boolean>(false);

  const loadOrgs = () => {
    setLoading(true);
    dispatch(
      getRisOrgs((result) => {
        setOrgs(result ?? []);
        setLoading(false);
      })
    );
  };

  useEffect(() => {
    if (!isCurrentAccountAdmin()) {
      navigate(-1);
      return;
    }
    loadOrgs();
  }, []);

  const search = searchText.trim().toLowerCase();
  const displayOrgs = search
    ? orgs.filter(
        (org) =>
          org.Name.toLowerCase().includes(search) ||
          (org.EmailSuffix ?? "").toLowerCase().includes(search)
      )
    : orgs;

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            width: "95%",
            height: "95%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AppLoader />
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: theme.page.bgColor,
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "1000px", p: 2 }}>
          <Box
            sx={{
              minHeight: "60px",
              width: "100%",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Hidden lgUp>
                <IconButton
                  sx={{ color: "text.secondary", marginLeft: 0, padding: 0, marginRight: 1.5 }}
                  edge="start"
                  className="menu-btn"
                  color="inherit"
                  aria-label="open drawer"
                  onClick={() => dispatch(toggleNavCollapsed())}
                  size="large"
                >
                  <MenuIcon sx={{ width: 35, height: 35 }} />
                </IconButton>
              </Hidden>
              <Box sx={{ py: 1 }}>
                <Typography
                  variant="h3"
                  component="h3"
                  color={fontColor.titleFontColor}
                  style={{ fontSize: 24 }}
                >
                  Organisations
                </Typography>
              </Box>
              <Box sx={{ ml: 2 }}>
                <Button
                  startIcon={<AddIcon />}
                  disabled={loading}
                  variant="contained"
                  onClick={() => {
                    setEditOrg(undefined);
                    setOpenOrgDialog(true);
                  }}
                  sx={{
                    width: isScreenLessThanSmall ? "90px" : "190px",
                    textTransform: "none",
                    borderRadius: 1,
                    color: "white",
                  }}
                >
                  {isScreenLessThanSmall ? "Create" : "Create Organisation"}
                </Button>
              </Box>
            </Box>
            <TextField
              id="search"
              type="search"
              label="Search"
              variant="standard"
              value={searchText}
              sx={{ width: isScreenLessThanSmall ? 100 : 200, mb: 1, ml: 1 }}
              onChange={(event) => setSearchText(event.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5, pr: 1 }}>
            <Typography color={"dimgrey"}>Total organisations: {displayOrgs.length}</Typography>
          </Box>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: theme.table.subHeaderBgColor }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email suffix</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody
                sx={{ "& .MuiTableRow-root:hover": { backgroundColor: theme.table.hoverBgColor } }}
              >
                {displayOrgs.length === 0 ? (
                  <TableRow>
                    <TableCell sx={{ p: 0 }} colSpan={3}>
                      <EmptyResult>{loading ? "" : "No organisations found"}</EmptyResult>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayOrgs.map((org) => (
                    <TableRow key={org.Id} hover>
                      <TableCell>{org.Name}</TableCell>
                      <TableCell>
                        {org.EmailSuffix ? (
                          `@${org.EmailSuffix}`
                        ) : (
                          <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
                            Any email
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          aria-label={`Edit ${org.Name}`}
                          size="small"
                          onClick={() => {
                            setEditOrg(org);
                            setOpenOrgDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <OrgDialog
        isOpen={openOrgDialog}
        org={editOrg}
        onClose={() => setOpenOrgDialog(false)}
        onSaved={() => {
          setOpenOrgDialog(false);
          loadOrgs();
        }}
      />
      <AppInfoView />
    </>
  );
};

export default OrganisationsPage;
